<script setup>
import { computed, ref, onMounted } from "vue";
import { marked } from "marked";
import LegalPanel from "@components/LegalPanel.vue";

const props = defineProps({
    title: { type: String, default: null },
    slug: { type: String, required: true },
    close: { type: Function, default: null },
});

const html = ref("");
const apiTitle = ref("");
const loading = ref(true);

const resolvedTitle = computed(() => props.title ?? apiTitle.value);

marked.setOptions({ gfm: true, breaks: true });

onMounted(async () => {
    try {
        const { data } = await window.axios.get(`/api/page/${props.slug}`);
        if (typeof data === "string") {
            html.value = data ? marked.parse(data) : "";
        } else {
            apiTitle.value = data?.title ?? "";
            const md = data?.content;
            html.value = md ? marked.parse(md) : "";
        }
    } finally {
        loading.value = false;
    }
});
</script>

<template>
    <LegalPanel :title="resolvedTitle" :close="close">
        <div v-if="loading" class="flex flex-1 items-center justify-center">
            <svg
                class="size-6 animate-spin text-yellow-400"
                viewBox="0 0 24 24"
                fill="none"
            >
                <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-opacity="0.25"
                />
                <path
                    d="M22 12a10 10 0 0 0-10-10"
                    stroke="currentColor"
                    stroke-width="3"
                    stroke-linecap="round"
                />
            </svg>
        </div>
        <section v-else class="markdown body-sm leading-[1.4] faded-text">
            <div v-html="html" />
        </section>
    </LegalPanel>
</template>

<style scoped>
.markdown :deep(h1),
.markdown :deep(h2),
.markdown :deep(h3),
.markdown :deep(h4) {
    font-size: var(--text-body-md, 1rem);
    line-height: 0.8;
    font-weight: 700;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 1rem;
}

.markdown :deep(p) {
    margin-bottom: 1rem;
}

.markdown :deep(p:last-child) {
    margin-bottom: 0;
}

.markdown :deep(ul) {
    list-style: disc;
    padding-left: 1.25rem;
    margin-bottom: 1rem;
}

.markdown :deep(strong) {
    color: #fff;
    font-weight: 600;
}
</style>
